import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <p className="text-xs text-gray-500 font-medium">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    <p className="text-xs text-gray-400 mt-1">{sub}</p>
  </div>
);

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview'|'billing'|'system'>('overview');

  const tabs = [['overview','Overview'],['billing','Billing & Pricing'],['system','System Status']] as const;
  const services = [
    { name: 'Frontend (Nginx)', status: 'healthy', port: '443' },
    { name: 'Python Backend (FastAPI)', status: 'healthy', port: '8000' },
    { name: 'PostgreSQL Database', status: 'healthy', port: '5432' },
    { name: 'Redis Cache', status: 'healthy', port: '6379' },
    { name: 'Celery Worker', status: 'healthy', port: '—' },
    { name: 'Celery Beat', status: 'healthy', port: '—' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
        <button onClick={() => navigate('/user-management')} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          Manage Users →
        </button>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map(([k,l]) => (
          <button key={k} onClick={() => setActiveTab(k)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab===k ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>{l}</button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Active Agents" value="13" sub="All operational" color="text-green-600" />
            <StatCard label="Total Users" value="4" sub="Amir, John, Shawn, Teresa" color="text-blue-600" />
            <StatCard label="Policies This Month" value="22" sub="+18% vs last month" color="text-purple-600" />
            <StatCard label="Platform Uptime" value="99.9%" sub="Last 30 days" color="text-green-600" />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'User Management', desc: 'Add, edit, reset passwords', path: '/user-management' },
                { label: 'Integrations', desc: 'API keys & third-party setup', path: '/integrations' },
                { label: 'Onboarding', desc: 'Agency setup wizard', path: '/onboarding' },
                { label: 'Compliance', desc: 'My Sentinel dashboard', path: '/compliance' },
                { label: 'AI Agents', desc: 'Activate & configure agents', path: '/ai-agents' },
                { label: 'Settings', desc: 'Platform preferences', path: '/settings' },
              ].map(a => (
                <button key={a.path} onClick={() => navigate(a.path)}
                  className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors">
                  <p className="text-sm font-medium text-gray-900">{a.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{a.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'billing' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Phoenix Rescue Apps Revenue Share</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-bold text-yellow-800">30% Platform Profit Share — Paid First on All Gross Revenue</p>
              <p className="text-xs text-yellow-700 mt-1">Phoenix Rescue Apps receives 30% of all gross platform revenue before any commissions or costs are calculated.</p>
            </div>
            <div className="space-y-3 text-sm">
              {[['Foundation Tier','$3,500/mo','2,000 validations/mo'],['Professional Tier','$5,500/mo','7,500 validations/mo'],['Enterprise Tier','$8,500/mo','15,000 validations/mo'],['Success Premium','$75 per policy','Per successfully bound policy'],['Audit Shield™ Certificate','$4.50 per cert','Per compliance certificate'],['Annual Compliance Monitoring','$75/yr per insured','Ongoing 50-state monitoring'],['Whale Commission Share','10% of first-year commission','Policies ≥ $15,000 premium'],].map(([k,v,d]) => (
                <div key={k} className="flex items-start justify-between py-2 border-b border-gray-100">
                  <div><p className="font-medium text-gray-900">{k}</p><p className="text-xs text-gray-500">{d}</p></div>
                  <p className="font-semibold text-blue-700 ml-4 whitespace-nowrap">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'system' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Docker Service Health</h3>
          <div className="space-y-2">
            {services.map(s => (
              <div key={s.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-gray-900">{s.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  {s.port !== '—' && <span className="text-xs text-gray-500">:{s.port}</span>}
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">{s.status}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700 font-medium">Server: Ubuntu 24.04.4 LTS · PHOENIXPC · lifeandwealthsolutions.online</p>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminPanel;
